import { useState } from 'react';
import type { OrgUnitNode } from './types';
import { ORG_UNIT_KIND_LABEL } from './types';

interface OrgUnitTreeProps {
  nodes: OrgUnitNode[];
  canEdit: boolean;
  selectedId: string | null;
  onSelect: (node: OrgUnitNode) => void;
  onAddChild: (parent: OrgUnitNode) => void;
  onEdit: (node: OrgUnitNode) => void;
  onReparent: (node: OrgUnitNode) => void;
  onDelete: (node: OrgUnitNode) => void;
}

function TreeNode({
  node,
  depth,
  canEdit,
  selectedId,
  onSelect,
  onAddChild,
  onEdit,
  onReparent,
  onDelete,
}: {
  node: OrgUnitNode;
  depth: number;
  canEdit: boolean;
  selectedId: string | null;
  onSelect: (node: OrgUnitNode) => void;
  onAddChild: (parent: OrgUnitNode) => void;
  onEdit: (node: OrgUnitNode) => void;
  onReparent: (node: OrgUnitNode) => void;
  onDelete: (node: OrgUnitNode) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const isSelected = selectedId === node.id;

  return (
    <div>
      <div
        className={`flex items-center gap-2 rounded px-2 py-1 ${
          isSelected
            ? 'bg-blue-50 dark:bg-blue-950/30'
            : 'hover:bg-gray-50 dark:hover:bg-muted/50'
        }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {node.children.length > 0 && (
          <button
            type='button'
            className='text-xs text-muted-foreground'
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? '▼' : '▶'}
          </button>
        )}
        <button type='button' className='flex-1 text-left text-sm' onClick={() => onSelect(node)}>
          <span className='text-muted-foreground'>{ORG_UNIT_KIND_LABEL[node.kind]}</span>
          <span className='ml-2 font-medium'>{node.name}</span>
          {node.code && <span className='ml-2 text-muted-foreground/70'>({node.code})</span>}
        </button>
        {canEdit && (
          <div className='flex gap-1'>
            <button
              type='button'
              className='text-xs text-blue-600'
              onClick={() => onAddChild(node)}
              title='Agregar hijo'
            >
              +
            </button>
            <button type='button' className='text-xs text-muted-foreground hover:text-foreground' onClick={() => onEdit(node)}>
              ✎
            </button>
            <button
              type='button'
              className='text-xs text-muted-foreground hover:text-foreground'
              onClick={() => onReparent(node)}
              title='Mover'
            >
              ↗
            </button>
            <button
              type='button'
              className='text-xs text-red-600'
              onClick={() => onDelete(node)}
            >
              ×
            </button>
          </div>
        )}
      </div>
      {expanded &&
        node.children.map(child => (
          <TreeNode
            key={child.id}
            node={child}
            depth={depth + 1}
            canEdit={canEdit}
            selectedId={selectedId}
            onSelect={onSelect}
            onAddChild={onAddChild}
            onEdit={onEdit}
            onReparent={onReparent}
            onDelete={onDelete}
          />
        ))}
    </div>
  );
}

export function OrgUnitTree(props: OrgUnitTreeProps) {
  if (props.nodes.length === 0) {
    return <p className='text-sm text-muted-foreground'>Sin nodos. Cree la jerarquía base.</p>;
  }

  return (
    <div className='rounded border border-border bg-card p-2'>
      {props.nodes.map(node => (
        <TreeNode key={node.id} node={node} depth={0} {...props} />
      ))}
    </div>
  );
}
